import { z } from 'zod';

// In a browser or the desktop app, the content security policy forbids evaluating strings as code.
// zod tests for that by trying, and the refusal is reported even though it is caught, so tell it
// not to try. The server keeps zod's faster compiled validators.
if (typeof (globalThis as { document?: unknown }).document !== 'undefined') {
  z.config({ jitless: true });
}

/** Sex used for range selection. `any` ranges apply to everyone. */
export const Sex = z.enum(['M', 'F', 'O']);
export type Sex = z.infer<typeof Sex>;

const rangeCommon = {
  /** Restrict this range to one sex; omitted means any. */
  sex: Sex.optional(),
  /** Inclusive age window in days (18 years = 6570). */
  ageMinDays: z.number().int().nonnegative().optional(),
  ageMaxDays: z.number().int().nonnegative().optional(),
  /** Printed text that overrides the generated one, e.g. "within 20 minutes". */
  display: z.string().optional(),
};

/** A labelled interval. `min` is inclusive and `max` exclusive unless flagged otherwise. */
export const Band = z.object({
  label: z.string().min(1),
  min: z.number().optional(),
  max: z.number().optional(),
  minExclusive: z.boolean().optional(),
  maxInclusive: z.boolean().optional(),
  /** How a value inside this band is flagged. */
  flag: z.enum(['normal', 'low', 'high', 'abnormal']),
});
export type Band = z.infer<typeof Band>;

/**
 * A reference range. The explicit `type` exists because the sample reports showed
 * bare numbers ("0.80") and one-sided ranges ("<40") that min/max columns cannot express.
 */
export const RangeSpec = z.discriminatedUnion('type', [
  z.object({ type: z.literal('between'), min: z.number(), max: z.number(), ...rangeCommon }),
  z.object({ type: z.literal('lt'), max: z.number(), ...rangeCommon }),
  z.object({ type: z.literal('lte'), max: z.number(), ...rangeCommon }),
  z.object({ type: z.literal('gt'), min: z.number(), ...rangeCommon }),
  z.object({ type: z.literal('gte'), min: z.number(), ...rangeCommon }),
  z.object({ type: z.literal('bands'), bands: z.array(Band).min(2), ...rangeCommon }),
  z.object({
    type: z.literal('qualitative'),
    expected: z.array(z.string().min(1)).min(1),
    ...rangeCommon,
  }),
  z.object({ type: z.literal('text'), ...rangeCommon }),
]);
export type RangeSpec = z.infer<typeof RangeSpec>;

export const ColumnRole = z.enum([
  'investigation',
  'result',
  'flag',
  'ref_min',
  'ref_max',
  'ref_range',
  'unit',
  'method',
  'custom',
]);
export type ColumnRole = z.infer<typeof ColumnRole>;

export const Column = z.object({
  id: z.string().min(1),
  role: ColumnRole,
  label: z.string(),
  /** Relative width used by the PDF layout. */
  width: z.number().positive().optional(),
});
export type Column = z.infer<typeof Column>;

const rowBase = {
  id: z.string().min(1),
  /** Free text for custom columns, keyed by column id. */
  cells: z.record(z.string(), z.string()).optional(),
};

export const TestRow = z.object({
  ...rowBase,
  kind: z.literal('test'),
  /** Short code used by formulas, e.g. HB, PCV, RBC. Unique within a template. */
  code: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use capital letters, digits and _; start with a letter'),
  label: z.string().min(1),
  valueType: z.enum(['numeric', 'text', 'option']).default('numeric'),
  options: z.array(z.string()).optional(),
  /**
   * For a pick-from-a-list test: the list is a set of suggestions, and any other value may be
   * typed too (e.g. "Nil", or a count such as "2-4"). Without it only the list can be chosen.
   */
  allowOther: z.boolean().optional(),
  /** Expression over other rows' codes, e.g. "PCV * 10 / RBC". Makes the row calculated. */
  formula: z.string().optional(),
  method: z.string().optional(),
  loinc: z.string().optional(),
  unit: z.string().optional(),
  decimals: z.number().int().min(0).max(6).optional(),
  required: z.boolean().default(false),
  ranges: z.array(RangeSpec).default([]),
  critical: z.object({ low: z.number().optional(), high: z.number().optional() }).optional(),
  printBold: z.boolean().optional(),
  hideIfEmpty: z.boolean().optional(),
  footnote: z.string().optional(),
});
export type TestRow = z.infer<typeof TestRow>;

export const SectionRow = z.object({ ...rowBase, kind: z.literal('section'), label: z.string() });
export const NoteRow = z.object({ ...rowBase, kind: z.literal('note'), text: z.string() });
export const NarrativeRow = z.object({
  ...rowBase,
  kind: z.literal('narrative'),
  label: z.string().optional(),
  /** Default text; the report can override it. Markdown subset (bold, lists, tables). */
  text: z.string().default(''),
});

export const Row = z.discriminatedUnion('kind', [TestRow, SectionRow, NoteRow, NarrativeRow]);
export type Row = z.infer<typeof Row>;

export const ExtraField = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]*$/),
  label: z.string().min(1),
  type: z.enum(['text', 'number', 'date', 'datetime']),
});
export type ExtraField = z.infer<typeof ExtraField>;

export const PerformedBy = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('in_house') }),
  z.object({ kind: z.literal('reference_lab'), partnerId: z.string() }),
]);

export const TemplateDoc = z
  .object({
    name: z.string().min(1),
    aliases: z.array(z.string()).default([]),
    department: z.string().optional(),
    tags: z.array(z.string()).default([]),
    sampleType: z.string().optional(),
    instrument: z.string().optional(),
    interpretation: z.string().optional(),
    columns: z.array(Column).min(1),
    rows: z.array(Row),
    extraFields: z.array(ExtraField).default([]),
    /** Never listed in a patient's "My reports" or summarised in messages. */
    sensitive: z.boolean().default(false),
    /** Always issued as its own report (e.g. cultures with long turnaround). */
    separateReport: z.boolean().default(false),
    /**
     * What the lab charges for this test, in rupees. Private to the lab: it is never printed or
     * shown to patients. A report keeps the price it had when the test was chosen.
     */
    price: z.number().min(0).max(10_000_000).optional(),
    /** Days from registering to the report being ready. 0 or empty = the same day. */
    turnaroundDays: z.number().int().min(0).max(60).optional(),
    performedBy: PerformedBy.default({ kind: 'in_house' }),
    print: z
      .object({
        newPage: z.boolean().default(false),
        /** Print ref_min + ref_max as one "Reference range" column. */
        mergeRefColumns: z.boolean().default(true),
      })
      .default({ newPage: false, mergeRefColumns: true }),
  })
  .superRefine((doc, ctx) => {
    const seen = new Set<string>();
    for (const row of doc.rows) {
      if (row.kind !== 'test') continue;
      if (seen.has(row.code)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate test code ${row.code}`,
          path: ['rows'],
        });
      }
      seen.add(row.code);
    }
    if (!doc.columns.some((c) => c.role === 'result')) {
      ctx.addIssue({
        code: 'custom',
        message: 'A template needs a Result column',
        path: ['columns'],
      });
    }
  });
export type TemplateDoc = z.infer<typeof TemplateDoc>;
export type TemplateDocInput = z.input<typeof TemplateDoc>;

/** A copy of the template without its price. Reports keep the layout, never what the lab charges. */
export function withoutPrice(doc: TemplateDoc): TemplateDoc {
  const { price: _price, ...rest } = doc;
  return rest;
}
